import { useState, useEffect } from "react";
import { useCustomerAuth } from "@/components/customer/CustomerAuthProvider";
import { useLocalization } from "@/hooks/useLocalization";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent } from "@/components/ui/dialog";

import { Calendar, Clock, MapPin, Download, Camera, CameraOff, ChevronRight, Share2, X } from "lucide-react";
import { format, parseISO, isBefore, startOfDay } from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "@/hooks/use-toast";

const getAppointmentTypeColor = (type: string) => {
  switch (type) {
    case 'consultation': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'fitting': return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'final_fitting': return 'bg-green-100 text-green-800 border-green-200';
    case 'alteration': return 'bg-orange-100 text-orange-800 border-orange-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getAppointmentTypeLabel = (type: string, t: (key: string) => string) => {
  switch (type) {
    case 'consultation': return t('consultation');
    case 'fitting': return t('fitting');
    case 'final_fitting': return t('final_fitting');
    case 'alteration': return t('alteration');
    default: return type;
  }
};

interface AppointmentPhoto {
  id: string;
  url: string;
  tag: string | null;
  is_public: boolean;
  created_at: string;
}

interface Appointment {
  id: string;
  title: string;
  description: string | null;
  start_time: string;
  end_time: string;
  type: 'consultation' | 'fitting' | 'final_fitting' | 'alteration';
  location: string | null;
  outcome: string | null;
  outcome_notes: string | null;
  photo_count: number;
  latest_photo_thumb: string | null;
  photos: AppointmentPhoto[];
}

interface OrganizationSettings {
  street_address: string | null;
  city: string | null;
  province: string | null;
  phone: string | null;
}

export default function CustomerAppointments() {
  const { customer, session } = useCustomerAuth();
  const { t, language } = useLocalization();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [organizationSettings, setOrganizationSettings] = useState<OrganizationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [filter, setFilter] = useState<'upcoming' | 'past'>('upcoming');
  const [fullScreenPhoto, setFullScreenPhoto] = useState<string | null>(null);

  useEffect(() => {
    if (customer) {
      console.log('Customer available, fetching appointments...');
      console.log('Customer:', customer);
      console.log('Session:', session);
      fetchAppointments();
    }
  }, [customer]);

  const fetchAppointments = async () => {
    try {
      console.log('Fetching appointments for customer:', customer?.id);
      
      if (!customer?.email) {
        console.error('No customer email available');
        toast({
          title: t('error'),
          description: t('customer_information_not_available'),
          variant: "destructive",
        });
        return;
      }
      
      console.log('Making API call for customer:', customer.email);
      
      const { data, error } = await supabase.functions.invoke(
        'customer-appointments',
        {
          body: {
            customer_email: customer.email,
            customer_id: customer.id,
            session_token: 'customer-portal-session',
          },
        }
      );

      if (error) {
        console.error('Error fetching appointments:', error);
        toast({
          title: t('error'),
          description: t('failed_to_load_appointments'),
          variant: "destructive",
        });
        return;
      }

      console.log('Appointments response:', data);
      setAppointments(data?.appointments || []);
      setOrganizationSettings(data?.organizationSettings || null);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      toast({
        title: t('error'),
        description: t('failed_to_load_appointments'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };



  const addToCalendar = (appointment: Appointment) => {
    const startDate = new Date(appointment.start_time);
    const endDate = new Date(appointment.end_time);
    
    const formatDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const title = encodeURIComponent(appointment.title);
    const description = encodeURIComponent(appointment.description || '');
    const location = encodeURIComponent(appointment.location || '');
    
    const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formatDate(startDate)}/${formatDate(endDate)}&details=${description}&location=${location}`;
    
    window.open(calendarUrl, '_blank');
  };

  const handlePhotoDownload = async (photoUrl: string, index: number) => {
    try {
      const response = await fetch(photoUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `appointment-photo-${index + 1}.jpg`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: t('success'),
        description: t('photo_downloaded')
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: t('error'),
        description: t('photo_download_failed'),
        variant: "destructive"
      });
    }
  };

  const handlePhotoShare = async (photoUrl: string) => {
    if (navigator.share) {
      try {
        const response = await fetch(photoUrl);
        const blob = await response.blob();
        const file = new File([blob], 'appointment-photo.jpg', { type: blob.type });
        
        await navigator.share({
          files: [file],
          title: t('appointment_photo'),
          text: t('check_out_photo')
        });
      } catch (error) {
        console.error('Share error:', error);
        // Fallback to download
        handlePhotoDownload(photoUrl, 0);
      }
    } else {
      // Fallback to download
      handlePhotoDownload(photoUrl, 0);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'consultation':
        return '💬';
      case 'fitting':
        return '📏';
      case 'final_fitting':
        return '✨';
      case 'alteration':
        return '🪡';
      default:
        return '📅';
    }
  };

  const getFilteredAppointments = () => {
    if (!appointments || !Array.isArray(appointments)) {
      return [];
    }
    
    const now = startOfDay(new Date());
    return appointments.filter(appointment => {
      const appointmentDate = startOfDay(new Date(appointment.start_time));
      if (filter === 'upcoming') {
        return !isBefore(appointmentDate, now);
      } else {
        return isBefore(appointmentDate, now);
      }
    });
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const filteredAppointments = getFilteredAppointments();

  return (
    <div className="container mx-auto px-4 py-6 max-w-2xl">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('my_appointments')}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('view_appointment_photos')}
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex bg-muted rounded-lg p-1">
          <button
            onClick={() => setFilter('upcoming')}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              filter === 'upcoming'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('upcoming')}
          </button>
          <button
            onClick={() => setFilter('past')}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              filter === 'past'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('past')}
          </button>
        </div>

        {/* Appointments List */}
        <div className="space-y-3">
          {filteredAppointments.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  {filter === 'upcoming' ? t('no_upcoming_appointments') : t('no_past_appointments')}
                </h3>
                <p className="text-muted-foreground">
                  {t('no_appointments_message').replace('{filter}', filter === 'upcoming' ? t('upcoming').toLowerCase() : t('past').toLowerCase())}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredAppointments.map((appointment) => (
              <Card 
                key={appointment.id} 
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setSelectedAppointment(appointment)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      {/* Date and Type */}
                      <div className="flex items-center gap-2 mb-2">
                         <Badge variant="secondary" className="text-xs">
                           {language === 'it' 
                             ? format(new Date(appointment.start_time), 'd MMM yyyy', { locale: it })
                             : format(new Date(appointment.start_time), 'MMM d, yyyy')
                           }
                         </Badge>
                        <span className="text-lg">{getTypeIcon(appointment.type)}</span>
                        <span className="text-sm font-medium text-foreground capitalize">
                          {getAppointmentTypeLabel(appointment.type, t)}
                        </span>
                      </div>

                      {/* Title and Location */}
                      <h3 className="font-medium text-foreground mb-1">{appointment.title}</h3>
                      {appointment.location && (
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          {appointment.location}
                        </div>
                      )}
                    </div>

                    {/* Photo Thumbnails */}
                    <div className="flex items-center gap-2">
                      {appointment.photo_count > 0 ? (
                        <div className="flex items-center gap-1">
                          {/* Thumbnail Stack */}
                          <div className="relative">
                            {appointment.latest_photo_thumb && (
                              <img
                                src={appointment.latest_photo_thumb}
                                alt="Appointment photo"
                                className="w-12 h-12 rounded-lg object-cover border border-border"
                              />
                            )}
                            {appointment.photo_count > 1 && (
                              <Badge 
                                variant="secondary" 
                                className="absolute -top-1 -right-1 text-xs px-1 h-5 min-w-5 flex items-center justify-center"
                              >
                                +{appointment.photo_count - 1}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <CameraOff className="h-4 w-4" />
                          <span className="text-xs">{t('no_photos_yet')}</span>
                        </div>
                      )}
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Appointment Detail Sheet */}
        {selectedAppointment && (
          <Sheet open={!!selectedAppointment} onOpenChange={() => setSelectedAppointment(null)}>
            <SheetContent side="bottom" className="h-[90vh] p-0">
              <div className="flex flex-col h-full">
                {/* Header */}
                <SheetHeader className="p-6 pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                       <SheetTitle className="text-left">
                         {format(new Date(selectedAppointment.start_time), 'EEEE, MMMM d, yyyy', { locale: language === 'it' ? it : undefined })}
                       </SheetTitle>
                      <p className="text-sm text-muted-foreground capitalize">
                        {getAppointmentTypeLabel(selectedAppointment.type, t)} • {format(new Date(selectedAppointment.start_time), 'h:mm a')}
                      </p>
                    </div>
                  </div>

                  {selectedAppointment.location && (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      {selectedAppointment.location}
                    </div>
                  )}

                  {selectedAppointment.description && (
                    <>
                      <Separator className="my-2" />
                      <p className="text-sm text-foreground text-left">{selectedAppointment.description}</p>
                    </>
                  )}
                </SheetHeader>

                {/* Photo Gallery */}
                <div className="flex-1 px-6 overflow-y-auto">
                  {selectedAppointment.photos && selectedAppointment.photos.length > 0 ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-foreground">
                          {t('photos')} ({selectedAppointment.photos.length})
                        </h4>
                      </div>

                      {/* Photo Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl">
                         {selectedAppointment.photos.map((photo, index) => (
                           <div key={photo.id} className="relative group">
                             <img
                               src={photo.url}
                               alt={`${photo.tag || 'Photo'} view`}
                               className="w-full aspect-square object-cover rounded-lg border border-border cursor-pointer hover:opacity-90 transition-opacity"
                               onClick={() => setFullScreenPhoto(photo.url)}
                             />
                            {photo.tag && (
                              <Badge className="absolute top-2 left-2 text-xs">
                                {photo.tag}
                              </Badge>
                            )}
                            
                            {/* Photo Actions Overlay */}
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePhotoDownload(photo.url, index);
                                }}
                                className="h-8 w-8 p-0"
                              >
                                <Download className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <Camera className="h-12 w-12 text-muted-foreground mb-4" />
                      <h4 className="font-medium text-foreground mb-2">{t('no_photos_yet')}</h4>
                      <p className="text-sm text-muted-foreground max-w-sm">
                        {t('photos_appear_here')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        )}

        {/* Full Screen Photo Dialog */}
        <Dialog open={!!fullScreenPhoto} onOpenChange={() => setFullScreenPhoto(null)}>
          <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 border-0">
            <div className="relative w-full h-full flex items-center justify-center bg-black/90">
              <img
                src={fullScreenPhoto || ""}
                alt="Full screen photo"
                className="max-w-full max-h-full object-contain"
                onClick={() => setFullScreenPhoto(null)}
              />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
